export interface Solution {
	AnalyticModels?: AnalyticModel[] | null;
	Data?: { [key: string]: string } | null;
	Guid?: string;
	Input?: Input | null;
	Name?: null | string;
	PhysicalModel?: PhysicalModel | null;
	ResultModels?: ResultModel[] | null;
}

export interface AnalyticModel {
	AnalyticElements?: AnalyticElement[] | null;
	Guid?: string;
	Name?: null | string;
	NamesOfAnalysesToBeUsed?: string[] | null;
	Obstructions?: Mesh[] | null;
}

export interface AnalyticElement {
	Guid?: string;
	IsObstacle?: boolean;
	Mesh?: Mesh | null;
	ParentGuid?: string;
	Samples?: Ray[] | null;
}

/**
 * Mesh geometry in 3d space
 */
export interface Mesh {
	/**
	 * Indices as list of integers
	 */
	Indices?: number[] | null;
	/**
	 * Vertices as list of Points
	 */
	Vertices?: Point[] | null;
}

/**
 * Point in 3d space
 *
 * Origin Point from which Ray starts
 */
export interface Point {
	/**
	 * X coordinate of a point
	 */
	X?: number;
	/**
	 * Y coordinate of a point
	 */
	Y?: number;
	/**
	 * Z coordinate of a point
	 */
	Z?: number;
}

/**
 * Ray in 3d space
 */
export interface Ray {
	/**
	 * Vector that determines Ray's direction
	 */
	Direction?: Vector;
	/**
	 * Origin Point from which Ray starts
	 */
	Origin?: Point;
}

/**
 * Vector that determines Ray's direction
 *
 * Vector in 3d space
 */
export interface Vector {
	/**
	 * X value
	 */
	X?: number;
	/**
	 * Y value
	 */
	Y?: number;
	/**
	 * Z value
	 */
	Z?: number;
}

export interface Input {
	InputModel?: InputModel | null;
	ProjectData?: ProjectData | null;
}

export interface InputModel {
	BuildingLines?: BuildingLine[] | null;
	BuildingZones?: BuildingZone[] | null;
	CheckZones?: CheckZone[] | null;
	ExcludedZones?: ExcludedZone[] | null;
	NeighbourSamples?: NeighbourSample[] | null;
	Surroundings?: Surrounding[] | null;
	Symbols?: Symbol[] | null;
	Terrain?: Terrain | null;
}

export interface BuildingLine {
	Guid?: string;
	ParentGuid?: string;
	Shape?: Polyline | null;
}

/**
 * Polyline in 3D space. For zone or parcel boundaries (Boundary, BottomOutline), use a
 * closed ring: first and last vertex should coincide. Open polylines (e.g.
 * BuildingLine.Shape) need at least two points.
 */
export interface Polyline {
	/**
	 * Vertex list. For closed boundaries, repeat the first point at the end (typical
	 * Clipper-style ring).
	 */
	Points?: Point[] | null;
}

export interface BuildingZone {
	Boundary?: Polyline | null;
	BuildingAtticHeight?: number;
	BuildingDepthEW?: Interval1D;
	BuildingDepthNS?: Interval1D;
	BuildingLength?: Interval1D;
	BuildingLevelHeights?: number[] | null;
	BuildingNumberOfLevels?: IntervalInt1D;
	BuildingOrientationAngle?: number;
	BuildingOrientationAngleDeviation?: number;
	Color?: string;
	FloorLevelHeight?: number;
	GroundFloorLevelHeight?: number;
	Guid?: string;
	MaxEavesHeight?: number;
	MaxRelativeBuildingHeight?: number;
	Name?: null | string;
	ParentGuid?: string;
	ParkingMultiplier?: ParkingMultiplier | null;
	RoofPitch?: Interval1D;
	RoofType?: RoofType;
	StructureIntervals?: StructureIntervals | null;
	StructureRatio?: StructureRatio | null;
	TopFloorLevelHeight?: number;
}

/**
 * One-dimensional Interval.
 *
 * Interval in X dimension
 *
 * Interval in Y dimension
 *
 * Tells what is the value's range
 */
export interface Interval1D {
	/**
	 * End of Interval
	 */
	End?: number;
	/**
	 * Start of Interval
	 */
	Start?: number;
}

/**
 * One-dimensional Interval with integers
 */
export interface IntervalInt1D {
	/**
	 * End of Interval
	 */
	End?: number;
	/**
	 * Start of Interval
	 */
	Start?: number;
}

export interface ParkingMultiplier {
	AdditionalPlaces?: number;
	AreaOfOneParkingPlace?: number;
	Flat0Bedroom?: number;
	Flat1Bedroom?: number;
	Flat2Bedrooms?: number;
	Flat3Bedrooms?: number;
	Flat4Bedrooms?: number;
	OtherFlat?: number;
}

export enum RoofType {
	Flat = "Flat",
	Gable = "Gable",
	Hip = "Hip",
}

export interface StructureIntervals {
	Flat0Bedroom?: Interval1D;
	Flat1Bedroom?: Interval1D;
	Flat2Bedrooms?: Interval1D;
	Flat3Bedrooms?: Interval1D;
	Flat4Bedrooms?: Interval1D;
}

export interface StructureRatio {
	Flat0Bedroom?: number;
	Flat1Bedroom?: number;
	Flat2Bedrooms?: number;
	Flat3Bedrooms?: number;
	Flat4Bedrooms?: number;
	OtherFlat?: number;
}

export interface CheckZone {
	Boundary?: Polyline | null;
	Color?: string;
	Guid?: string;
	MaxBuildingCoverageRatio?: number;
	MaxFloorAreaRatioAboveGround?: number;
	MaxFloorAreaRatioUnderground?: number;
	Name?: null | string;
	ParentGuid?: string;
}

export interface ExcludedZone {
	Boundary?: Polyline | null;
	Color?: string;
	Guid?: string;
	Name?: null | string;
	ParentGuid?: string;
}

export interface NeighbourSample {
	Guid?: string;
	Name?: null | string;
	ParentGuid?: string;
	Ray?: Ray | null;
}

export interface Surrounding {
	Guid?: string;
	Mesh?: Mesh | null;
	Name?: null | string;
	ParentGuid?: string;
	Polylines?: Polyline[] | null;
}

export interface Symbol {
	Guid?: string;
	Mesh?: Mesh | null;
	Name?: null | string;
	ParentGuid?: string;
}

export interface Terrain {
	Guid?: string;
	Mesh?: Mesh | null;
	Name?: null | string;
	ParentGuid?: string;
	ValueMap2D?: ValueMap2D | null;
}

/**
 * It's a 2D map of values
 */
export interface ValueMap2D {
	/**
	 * Tells what is the axis aligned size of a map
	 */
	Interval2D?: Interval2D;
	/**
	 * Tells what is the value's range
	 */
	ValueInterval?: Interval1D;
	/**
	 * Values in 2D space
	 */
	Values2D?: Value2D[] | null;
}

/**
 * Tells what is the axis aligned size of a map
 *
 * Two-dimensional Interval
 */
export interface Interval2D {
	/**
	 * Interval in X dimension
	 */
	IntervalX?: Interval1D;
	/**
	 * Interval in Y dimension
	 */
	IntervalY?: Interval1D;
}

/**
 * Value with it's own 2D position
 */
export interface Value2D {
	/**
	 * Position in 2D space
	 */
	Position?: ValueTupleOfDoubleAndDouble;
	/**
	 * Value on a 2D space
	 */
	Value?: number;
}

/**
 * Position in 2D space
 */
export interface ValueTupleOfDoubleAndDouble {
	Item1?: number;
	Item2?: number;
}

export interface ProjectData {
	AnalyzeDaylight?: boolean;
	AnalyzeObstruction?: boolean;
	DaylightTimeRequirements?: TimeRequirement[] | null;
	FlatsAreaRatio?: number;
	Guid?: string;
	IsDowntown?: boolean;
	LocationOnEarth?: LocationOnEarth | null;
	MinimumDistanceBetweenBuildings?: number;
	ProjectName?: null | string;
	TrueNorth?: TrueNorth | null;
}

export interface TimeRequirement {
	TimeIntervalAvailable?: TimeInterval | null;
	TimeSpanToPass?: TimeSpan;
	TimeZoneInfoIdUsedInUIOnly?: null | string;
}

export interface TimeInterval {
	End?: Date;
	Start?: Date;
}

export interface TimeSpan {
	Milliseconds?: number;
}

export interface LocationOnEarth {
	Latitude?: number;
	Longitude?: number;
}

export interface TrueNorth {
	AngleBetweenWorldYAndTrueNorth?: number;
}

export interface PhysicalModel {
	BuildingAxes?: BuildingAxis[] | null;
	BuildingMasses?: BuildingMass[] | null;
	BuildingRails?: BuildingRail[] | null;
	Parcels?: Parcel[] | null;
	SpaceMasses?: SpaceMass[] | null;
}

export interface BuildingAxis {
	BuildingPosition?: BuildingPosition;
	Guid?: string;
	ParentGuid?: string;
	Shape?: Polyline | null;
}

export enum BuildingPosition {
	Left = "Left",
	Middle = "Middle",
	Right = "Right",
}

export interface BuildingMass {
	BottomOutline?: Polyline | null;
	Guid?: string;
	LevelHeights?: number[] | null;
	ParentGuid?: string;
	RoofRepresentation?: Mesh | null;
}

export interface BuildingRail {
	BuildingPosition?: BuildingPosition;
	Guid?: string;
	ParentGuid?: string;
	Shape?: Polyline | null;
}

export interface Parcel {
	Boundary?: Polyline | null;
	Guid?: string;
	ParentGuid?: string;
}

export interface SpaceMass {
	BottomOutline?: Polyline | null;
	Guid?: string;
	Level?: number;
	LevelHeights?: number[] | null;
	ParentGuid?: string;
	SpaceType?: SpaceType;
}

export enum SpaceType {
	Circulation = "Circulation",
	Core = "Core",
	Flat0Bedroom = "Flat0Bedroom",
	Flat1Bedroom = "Flat1Bedroom",
	Flat2Bedrooms = "Flat2Bedrooms",
	Flat3Bedrooms = "Flat3Bedrooms",
	Flat4Bedrooms = "Flat4Bedrooms",
	NotFlat = "NotFlat",
	OtherFlat = "OtherFlat",
}

export interface ResultModel {
	Guid?: string;
	NameOfAnalysisUsed?: null | string;
	ParentGuid?: string;
	ResultElements?: ResultElement[] | null;
}

export interface ResultElement {
	Guid?: string;
	Mesh?: Mesh | null;
	ParentGuid?: string;
	RayResults?: RayResult[] | null;
	Result?: Result | null;
	ResultConclusion?: ResultConclusion;
}

/**
 * Data container for single Ray
 */
export interface RayResult {
	/**
	 * Text that helps to identify this RayResult
	 */
	Identifier?: null | string;
	/**
	 * Ray
	 */
	Ray?: Ray | null;
	/**
	 * State of the RayResult
	 */
	State?: RayResultState;
}

/**
 * State of the RayResult
 */
export enum RayResultState {
	Hit = "Hit",
	HitButTooFar = "HitButTooFar",
	Miss = "Miss",
	Ready = "Ready",
}

/**
 * Result that contain different numerical values for different factors that were measured.
 */
export interface Result {
	/**
	 * List of factors which are the results of measurement / analysis.
	 */
	Factors?: { [key: string]: number } | null;
	/**
	 * Identifier for Result, so it makes more clear e.g. for what object these Results are
	 * created. Can be a name,
	 * a description, or some ID or GUID.
	 */
	Identifier?: null | string;
}

export enum ResultConclusion {
	Accepted = "Accepted",
	Rejected = "Rejected",
	Unknown = "Unknown",
}
